"use client"
import { useMemo } from "react"
import { Edit3, LayoutGrid, ArrowUpRight } from "lucide-react"
import {
  Archive,
  Briefcase,
  Buildings,
  Calculator,
  ChartBar,
  Database,
  Desktop,
  FileText,
  Gear,
  Globe,
  Lock,
  MapTrifold,
  ShieldCheck,
  TrendUp,
  UserCheck,
  Users,
  Wrench,
} from "@phosphor-icons/react"
import { smartCase } from "../utils/text"

// Bandeja crema cálida (sin borde) sobre la que descansan las tarjetas blancas
const SECTION_CLASS = "rounded-2xl bg-cafe-100/25 p-6 sm:p-8"
// Cuadro del icono: café intermedio (más suave que el café casi negro de la paleta)
const ICON_TILE_ACTIVE = "bg-[#5D4037] text-white group-hover:bg-[#6D4C41] group-hover:text-sol-300"
const CARD_SURFACE =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sol-400 bg-white shadow-[0_1px_2px_rgba(62,39,35,0.05),0_10px_24px_-14px_rgba(62,39,35,0.18)] hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(62,39,35,0.05),0_16px_32px_-18px_rgba(62,39,35,0.22)]"

// Luz cálida que sigue al cursor dentro de la tarjeta; la posición va en variables CSS
// para no provocar renders de React en cada movimiento del mouse
const SPOTLIGHT_STYLE = {
  background:
    "radial-gradient(320px circle at var(--mx, 50%) var(--my, 50%), rgba(243, 161, 8, 0.08), transparent 70%)",
}

const trackPointer = (e) => {
  const rect = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`)
  e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`)
}

const TIME_FORMATTER = new Intl.DateTimeFormat("es-HN", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
  timeZone: "America/Tegucigalpa",
})

const getTimeParts = (date) => {
  const parts = TIME_FORMATTER.formatToParts(date)
  const pick = (type) => parts.find((part) => part.type === type)?.value ?? ""
  return { hour: pick("hour"), minute: pick("minute"), period: pick("dayPeriod") }
}

const toTitleCase = (text = "") =>
  text.toLowerCase().replace(/(^|\s)\S/g, (char) => char.toUpperCase())

const DesktopView = ({
  user,
  currentTime,
  getGreeting,
  applications,
  getTodayTasks,
  editTask,
  accessApplication,
  notifications = [],
  onShowNotifications,
}) => {
  // Función de iconos memorizada
  const getAppIconComponent = useMemo(() => {
    const iconMap = {
      Building: Buildings,
      Wrench: Wrench,
      Tool: Wrench,
      Tools: Wrench,
      Users: Users,
      UserCheck: UserCheck,
      Map: MapTrifold,
      FileText: FileText,
      Globe: Globe,
      Calculator: Calculator,
      Archive: Archive,
      Briefcase: Briefcase,
      Project: Briefcase,
      BarChart3: ChartBar,
      Shield: ShieldCheck,
      TrendingUp: TrendUp,
      Database: Database,
      Settings: Gear,
      Desktop: Desktop,
      Monitor: Desktop,
    }

    return (app) => {
      // Usar ícono de la API si existe
      const iconFromAPI = app.Icon || app.icon
      if (iconFromAPI && iconMap[iconFromAPI]) {
        return iconMap[iconFromAPI]
      }

      // Fallback basado en nombre
      const appName = (app.name || app.NombreAplicacion || "").toLowerCase()

      if (appName.includes("administracion") || appName.includes("admin") || appName.includes("it")) return Database
      if (appName.includes("mantenimiento")) return Wrench
      if (appName.includes("proyectos")) return Briefcase
      if (appName.includes("empleados")) return UserCheck
      if (appName.includes("representantes") || appName.includes("gremiales")) return Users
      if (appName.includes("sigev") || appName.includes("vial") || appName.includes("mapa")) return MapTrifold
      if (appName.includes("contabilidad") || appName.includes("finanzas")) return Calculator
      if (appName.includes("archivo")) return Archive
      if (appName.includes("reporte")) return ChartBar
      if (appName.includes("seguridad")) return ShieldCheck
      if (appName.includes("web") || appName.includes("portal")) return Globe

      return Gear
    }
  }, [])

  const applicationsWithIcons = useMemo(() => {
    return applications.map(app => ({
      ...app,
      IconComponent: getAppIconComponent(app)
    }))
  }, [applications, getAppIconComponent])

  const getStatusText = (status) => {
    switch (status) {
      case "pending": return "Pendiente"
      case "inProgress": return "En progreso"
      case "completed": return "Realizada"
      default: return "Sin estado"
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "inProgress": return "bg-sol-200/25 text-cafe-600"
      case "completed": return "bg-green-50 text-green-700"
      default: return "bg-neutral-50/60 text-neutral-400"
    }
  }

  const handleAppClick = (app, hasAccess, isInactive) => (e) => {
    if (isInactive) {
      e.preventDefault()
      alert(`La aplicación ${app.name} está temporalmente deshabilitada. El equipo de IT le notificará cuando esté disponible nuevamente.`)
      return
    }
    if (hasAccess) {
      accessApplication(app, e)
    }
  }

  const todayTasks = useMemo(() => getTodayTasks(), [getTodayTasks])

  const unreadCount = useMemo(() => notifications.filter((notif) => !notif.read).length, [notifications])

  const firstName = toTitleCase((user?.empleado?.NombreEmpleado || user?.usuario || "").split(" ")[0])

  const timeParts = getTimeParts(currentTime)

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      {/* Encabezado de bienvenida */}
      <header className="pb-2 pt-4 text-center sm:pt-8">
        <p className="text-sm text-neutral-300">
          <span className="inline-block first-letter:uppercase">
            {currentTime.toLocaleDateString("es-CO", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </span>
          <span className="mx-2 text-cafe-200">·</span>
          <span className="tabular-nums">
            {timeParts.hour}:{timeParts.minute} {timeParts.period}
          </span>
        </p>

        <h2 className="mt-3 font-display text-4xl font-semibold leading-[1.1] tracking-tight text-cafe-700 sm:text-5xl">
          {getGreeting()},{" "}
          <span className="font-normal italic text-cafe-500">{firstName}</span>
        </h2>

        {/* Aviso de notificaciones: solo aparece si hay algo pendiente */}
        {unreadCount > 0 && onShowNotifications && (
          <button
            type="button"
            onClick={onShowNotifications}
            className="group mt-5 inline-flex items-center gap-2 text-sm font-medium text-cafe-600 transition-colors duration-200 hover:text-cafe-800"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-sol-400" />
            {unreadCount === 1 ? "1 notificación sin leer" : `${unreadCount} notificaciones sin leer`}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
        )}
      </header>

      {/* Divisoria entre el saludo y las secciones */}
      <div aria-hidden="true" className="h-px bg-gradient-to-r from-transparent via-cafe-300/80 to-transparent" />

      {/* Catálogo de aplicaciones */}
      <section className={SECTION_CLASS}>
        <h3 className="mb-8 text-center font-display text-2xl font-semibold tracking-tight text-cafe-700">Catálogo de aplicaciones</h3>

        <div className="grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {applicationsWithIcons.map((app, index) => {
            const isInactive = app.isInactive
            const hasAccess = app.hasLink && app.secureAccess && !isInactive
            const IconComponent = app.IconComponent
            const description = isInactive
              ? "Temporalmente inhabilitada. El equipo de IT le avisará cuando esté disponible."
              : smartCase(app.description, { sentence: true })

            return (
              <button
                key={app.id}
                type="button"
                onClick={handleAppClick(app, hasAccess, isInactive)}
                aria-disabled={!hasAccess}
                onMouseMove={hasAccess ? trackPointer : undefined}
                // Escalonado con tope para que catálogos largos no tarden en aparecer
                style={{ "--i": Math.min(index, 12) }}
                className={`portal-rise group relative flex w-full flex-col gap-3 overflow-hidden rounded-xl p-4 text-left transition-all duration-500 ${
                  hasAccess ? `cursor-pointer ${CARD_SURFACE}` : "cursor-not-allowed bg-white/50"
                }`}
              >
                {/* Luz dorada que sigue al cursor */}
                {hasAccess && (
                  <span
                    aria-hidden="true"
                    style={SPOTLIGHT_STYLE}
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  />
                )}

                <span className="relative flex min-h-[3.75rem] flex-col items-center justify-center px-7 text-center">
                  <span
                    className={`line-clamp-2 font-display text-[17px] font-semibold leading-snug tracking-tight ${
                      hasAccess ? "text-cafe-700" : "text-neutral-300"
                    }`}
                  >
                    {/* Subrayado dorado que se dibuja de izquierda a derecha */}
                    <span
                      className={
                        hasAccess
                          ? "box-decoration-clone bg-gradient-to-r from-sol-400/70 to-sol-400/70 bg-[length:0%_1px] bg-left-bottom bg-no-repeat pb-0.5 transition-[background-size] duration-700 ease-out group-hover:bg-[length:100%_1px]"
                          : ""
                      }
                    >
                      {smartCase(app.name)}
                    </span>
                  </span>
                  <span className="mt-0.5 block max-w-full truncate text-[13px] font-medium text-cafe-500">
                    {smartCase(app.unidadAdministrativa)}
                  </span>
                </span>

                <span
                  aria-hidden="true"
                  className="relative h-px w-full bg-gradient-to-r from-transparent via-cafe-200/70 to-transparent"
                />

                <span className="relative flex flex-1 items-center gap-3">
                  <span
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border transition-colors duration-500 ${
                      hasAccess
                        ? `border-transparent ${ICON_TILE_ACTIVE}`
                        : "border-neutral-50 bg-neutral-50/60 text-neutral-200"
                    }`}
                  >
                    <IconComponent className="h-5 w-5" weight="duotone" />
                  </span>
                  <span title={description} className="line-clamp-4 min-w-0 flex-1 text-left text-[15px] leading-relaxed text-neutral-300">
                    {description}
                  </span>
                </span>

                {/* El estado solo se muestra cuando la app no está disponible */}
                {!hasAccess && (
                  <span className="relative flex items-center justify-center gap-1.5 text-xs font-medium text-neutral-300">
                    {isInactive ? (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-neutral-200" />
                        Inhabilitada
                      </>
                    ) : (
                      <>
                        <Lock className="h-3.5 w-3.5" weight="duotone" />
                        Restringida
                      </>
                    )}
                  </span>
                )}

                {hasAccess && (
                  <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-cafe-700/10 text-cafe-700/80 transition-colors duration-500 group-hover:bg-sol-400/30 group-hover:text-cafe-700">
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-y-px group-hover:translate-x-px" />
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {applications.length === 0 && (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-cafe-100/40">
              <LayoutGrid className="h-7 w-7 text-cafe-700" />
            </div>
            <h3 className="mb-1 text-lg font-bold text-cafe-800 tracking-tight">No hay aplicaciones disponibles</h3>
            <p className="text-sm text-neutral-300">Contacte al administrador para que le asigne aplicaciones.</p>
          </div>
        )}

        {applications.length > 0 && (
          <p className="mt-6 text-right text-xs text-neutral-400">
            {applications.length} disponibles
          </p>
        )}
      </section>

      {/* Tareas de hoy: solo aparece si hay algo programado */}
      {todayTasks.length > 0 && (
      <section className={SECTION_CLASS}>
        <div className="mb-6 text-center">
          <h3 className="font-display text-2xl font-semibold tracking-tight text-cafe-700">Tareas de hoy</h3>
          <p className="mt-0.5 text-sm text-neutral-300">{todayTasks.length} programadas</p>
        </div>

          <ul className="space-y-3">
            {todayTasks.slice(0, 5).map((task, index) => (
              <li
                key={task.id}
                style={{ "--i": index }}
                className="portal-rise group relative flex items-center gap-4 rounded-lg bg-white px-4 py-3 shadow-[0_1px_2px_rgba(62,39,35,0.05),0_10px_24px_-14px_rgba(62,39,35,0.18)]"
              >
                {task.priority === "high" && (
                  <span
                    className="absolute left-0 top-3 bottom-3 w-0.5 rounded-full bg-secondary-700"
                    title="Prioridad alta"
                  />
                )}
                <div className="min-w-0 flex-1 select-none">
                  <p className={`text-sm font-medium ${task.completed ? "text-neutral-300 line-through" : "text-cafe-800"}`}>
                    {task.text}
                  </p>
                  <span className={`mt-1.5 inline-block rounded-md px-2 py-0.5 text-xs font-medium ${getStatusColor(task.status)}`}>
                    {getStatusText(task.status)}
                  </span>
                </div>
                <button
                  onClick={() => editTask(task)}
                  title="Editar tarea"
                  aria-label="Editar tarea"
                  className="rounded-lg p-2 text-neutral-300 transition-all duration-200 hover:bg-cafe-100/50 hover:text-cafe-700 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
      </section>
      )}
    </div>
  )
}

export default DesktopView
